// src/pages/ReportDelivery.js
// HomeIQa — Report download and upsell page
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import Logo from '../components/Logo';

export default function ReportDelivery() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadReport() {
      try {
        const snap = await getDoc(doc(db, 'reports', reportId));
        if (!snap.exists()) { setError('Report not found.'); return; }
        setReport(snap.data());
      } catch (e) {
        setError('Could not load your report. Please contact support@homeIQa.com');
      } finally {
        setLoading(false);
      }
    }
    loadReport();
  }, [reportId]);

  if (loading) return (
    <div style={styles.page}>
      <div style={styles.header}><Logo /></div>
      <div style={styles.center}><div style={styles.spinner} /></div>
    </div>
  );

  if (error) return (
    <div style={styles.page}>
      <div style={styles.header}><Logo /></div>
      <div style={styles.errorCard}>{error}</div>
    </div>
  );

  const { address, downloadURL, propertyData, reportContent } = report;

  const ADDONS = [
    { label: 'Crime, schools & livability report', price: 9, desc: 'Safety scores, school ratings, walkability — a must for buyers.' },
    { label: 'Flood & climate risk report', price: 12, desc: 'FEMA zone, wildfire, heat risk — critical for insurance decisions.' },
    { label: 'Permit & lien history check', price: 14, desc: 'Every permit, lien, and ownership record since construction.' }
  ];

  return (
    <div style={styles.page}>
      <div style={styles.header}><Logo /></div>

      {/* SUCCESS HERO */}
      <div style={styles.hero}>
        <div style={styles.heroCheck}>✓</div>
        <h1 style={styles.heroTitle}>Your HomeIQa report is ready</h1>
        <p style={styles.heroSub}>{address}</p>
        <a href={downloadURL} target="_blank" rel="noopener noreferrer" style={styles.downloadBtn}>
          Download Your Full Report (PDF) ↓
        </a>
        <p style={styles.emailNote}>A copy has also been sent to your email.</p>
      </div>

      <div style={styles.layout}>
        {/* SNAPSHOT */}
        <div style={styles.snapshotCard}>
          <div style={styles.cardTitle}>Your report snapshot</div>
          <div style={styles.bigValue}>
            ${propertyData?.estimatedValue?.toLocaleString() || '—'}
          </div>
          <div style={styles.bigValueSub}>
            Range: ${propertyData?.valueRangeLow?.toLocaleString()} – ${propertyData?.valueRangeHigh?.toLocaleString()}
          </div>

          <div style={styles.metricGrid}>
            {[
              { label: 'Confidence', value: `${propertyData?.confidenceScore || '—'}%` },
              { label: 'Best to list', value: reportContent?.timingBestMonths?.[0] || 'Spring' },
              { label: 'Market verdict', value: reportContent?.marketVerdict || '—' },
              { label: 'Est. net proceeds', value: propertyData?.estimatedValue ? `$${Math.round(propertyData.estimatedValue * 0.92).toLocaleString()}` : '—' }
            ].map(m => (
              <div key={m.label} style={styles.metric}>
                <div style={styles.metricLabel}>{m.label}</div>
                <div style={styles.metricVal}>{m.value}</div>
              </div>
            ))}
          </div>

          {reportContent?.valuationSummary && (
            <div style={styles.insightBox}>
              <strong>Key insight:</strong> {reportContent.valuationSummary}
            </div>
          )}
        </div>

        {/* ADDONS UPSELL */}
        <div style={styles.addonsCard}>
          <div style={styles.cardTitle}>Want deeper insight?</div>
          <div style={styles.addonsSubtitle}>
            Add any of these reports for a one-time fee. Delivered to your email instantly.
          </div>
          {ADDONS.map(addon => (
            <div key={addon.label} style={styles.addonItem}>
              <div>
                <div style={styles.addonName}>{addon.label}</div>
                <div style={styles.addonDesc}>{addon.desc}</div>
              </div>
              <button
                style={styles.addonBtn}
                onClick={() => navigate(`/start?addon=${encodeURIComponent(addon.label)}&address=${encodeURIComponent(address)}`)}
              >
                +${addon.price}
              </button>
            </div>
          ))}

          <div style={styles.appraisalBox}>
            <div style={styles.appraisalTitle}>Need an official appraisal?</div>
            <p style={styles.appraisalText}>
              Our network of ASC-verified licensed appraisers can provide a USPAP-compliant appraisal for pre-listing, PMI removal, estate, or divorce proceedings.
            </p>
            <button
              style={styles.appraisalBtn}
              onClick={() => navigate('/appraisers')}
            >
              Find a verified appraiser →
            </button>
          </div>
        </div>
      </div>

      <div style={styles.footer}>
        <Logo dark />
        <div style={styles.footerLinks}>
          <a href="/terms" style={styles.footerLink}>Terms</a>
          <a href="/refund-policy" style={styles.footerLink}>Refund Policy</a>
          <a href="mailto:support@homeIQa.com" style={styles.footerLink}>Support</a>
        </div>
        <div style={styles.footerNote}>
          © 2026 HomeIQa. This report is for informational purposes only.
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#FAF7F2', fontFamily: "'DM Sans', sans-serif" },
  header: { background: '#4A1220', borderBottom: '4px solid #C9A84C', padding: '14px 32px' },
  center: { display: 'flex', justifyContent: 'center', padding: '80px 0' },
  spinner: { width: 40, height: 40, border: '3px solid #EDE8E0', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  errorCard: { maxWidth: 480, margin: '60px auto', padding: '28px', background: '#FEF0F0', borderRadius: 10, fontSize: 14, color: '#8A1A1A', textAlign: 'center' },
  hero: { background: '#4A1220', padding: '48px 32px', textAlign: 'center', borderBottom: '4px solid #C9A84C' },
  heroCheck: { width: 52, height: 52, borderRadius: '50%', background: '#1D9E75', color: 'white', fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontWeight: 700 },
  heroTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 30, fontWeight: 500, color: 'white', marginBottom: 6 },
  heroSub: { fontSize: 14, color: 'rgba(255,255,255,0.55)', marginBottom: 24 },
  downloadBtn: { display: 'inline-block', background: '#C9A84C', color: '#4A1220', fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 500, padding: '14px 32px', borderRadius: 8, textDecoration: 'none', marginBottom: 12 },
  emailNote: { fontSize: 12, color: 'rgba(255,255,255,0.4)' },
  layout: { maxWidth: 900, margin: '40px auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, padding: '0 24px' },
  snapshotCard: { background: 'white', borderRadius: 12, padding: '28px 32px', border: '1px solid rgba(107,30,46,0.1)', alignSelf: 'start' },
  cardTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 500, color: '#4A1220', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid rgba(107,30,46,0.08)' },
  bigValue: { fontFamily: "'Cormorant Garamond', serif", fontSize: 48, fontWeight: 600, color: '#6B1E2E', lineHeight: 1, marginBottom: 4 },
  bigValueSub: { fontSize: 12, color: '#5A5A5A', marginBottom: 20, padding: '5px 10px', background: '#F7F0DC', display: 'inline-block', borderLeft: '3px solid #C9A84C' },
  metricGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 },
  metric: { background: '#FAF7F2', padding: '12px', borderRadius: 8 },
  metricLabel: { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9A9A9A', marginBottom: 4 },
  metricVal: { fontSize: 15, fontWeight: 500, color: '#1C1C1C' },
  insightBox: { background: '#F7F0DC', borderLeft: '3px solid #C9A84C', padding: '12px 14px', borderRadius: '0 6px 6px 0', fontSize: 13, color: '#5A3A00', lineHeight: 1.6 },
  addonsCard: { background: 'white', borderRadius: 12, padding: '28px 32px', border: '1px solid rgba(107,30,46,0.1)', alignSelf: 'start' },
  addonsSubtitle: { fontSize: 13, color: '#5A5A5A', marginBottom: 18, lineHeight: 1.55 },
  addonItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 0', borderBottom: '1px solid rgba(107,30,46,0.08)' },
  addonName: { fontSize: 13, fontWeight: 500, color: '#1C1C1C', marginBottom: 2 },
  addonDesc: { fontSize: 11, color: '#9A9A9A', lineHeight: 1.4 },
  addonBtn: { background: '#6B1E2E', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', flexShrink: 0, fontFamily: "'DM Sans', sans-serif" },
  appraisalBox: { marginTop: 20, padding: '18px', background: '#FAF7F2', borderRadius: 8, border: '1px solid rgba(107,30,46,0.1)' },
  appraisalTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 500, color: '#4A1220', marginBottom: 6 },
  appraisalText: { fontSize: 12, color: '#5A5A5A', lineHeight: 1.6, marginBottom: 12 },
  appraisalBtn: { background: 'transparent', border: '1.5px solid #6B1E2E', color: '#6B1E2E', borderRadius: 6, padding: '9px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  footer: { background: '#4A1220', padding: '20px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 48 },
  footerLinks: { display: 'flex', gap: 24 },
  footerLink: { fontSize: 12, color: 'rgba(255,255,255,0.4)', textDecoration: 'none' },
  footerNote: { fontSize: 11, color: 'rgba(255,255,255,0.25)' }
};
